import { createContext, useContext, useState, useCallback, PropsWithChildren, FC } from 'react';
import ConfirmDialog, { ConfirmDialogProps } from '../../components/ConfirmDialog';

interface ConfirmDialogContextValue {
    showConfirm: (
        props: Omit<ConfirmDialogProps, 'onCancel' | 'onOk'> & {
            onOk: () => void | Promise<void>;
        }
    ) => void;
    hideConfirm: () => void;
}

const ConfirmDialogContext = createContext<ConfirmDialogContextValue | null>(null);

const ConfirmDialogProvider: FC<PropsWithChildren> = ({ children }) => {
    const [dialogProps, setDialogProps] = useState<ConfirmDialogProps | null>(null);

    const hideConfirm = useCallback(() => {
        setDialogProps(null);
    }, []);

    const showConfirm = useCallback(
        (
            props: Omit<ConfirmDialogProps, 'onCancel' | 'onOk'> & {
                onOk: () => void | Promise<void>;
            }
        ) => {
            setDialogProps({
                ...props,
                onCancel: hideConfirm,
                onOk: () => {
                    const result = props.onOk();
                    Promise.resolve(result).finally(hideConfirm);
                },
            });
        },
        [hideConfirm]
    );

    return (
        <ConfirmDialogContext.Provider value={{ showConfirm, hideConfirm }}>
            {children}
            {dialogProps && <ConfirmDialog {...dialogProps} />}
        </ConfirmDialogContext.Provider>
    );
};

const useConfirmDialogContext = (): ConfirmDialogContextValue => {
    const context = useContext(ConfirmDialogContext);
    if (!context) {
        throw new Error('useConfirmDialogContext must be used within a ConfirmDialogProvider');
    }
    return context;
};

export { ConfirmDialogProvider };
export { useConfirmDialogContext };
